/**
 * CR402 Phase 2: Draft Component Preview
 * CR406: Wrapped in preview-only ErrorBoundary - never break Studio
 * CR305: Ribbon preview uses Theme Labs SSOT via useGlobalTheme
 * 
 * Renders a single component in isolation for real-time preview.
 * Shows the component with current draft configuration.
 * 
 * Note: Uses simplified preview that doesn't require full block rendering context.
 */

import React, { Component, type ReactNode, type ErrorInfo, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import type { ComponentsConfig } from "./DealCardsTab";
import type { CardStyling } from "./CardStylingPanel";
import { AlertCircle, Info, Star, Tags, LayoutTemplate, Type, Boxes, TrendingUp, DollarSign, Gift, Calendar, MousePointer, ExternalLink, Sparkles, ThumbsUp, ThumbsDown, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { ValueStarburstBadge } from "@/components/deals/ValueStarburstBadge";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { useGlobalTheme } from "@/providers/GlobalThemeProvider";
import { getResolvedRibbonHex, type RibbonColorConfig } from "@/lib/deal-cards/resolveRibbonColor";
import type { DealCategory } from "@/lib/deals/dealTypeRegistry";

// ================================================================
// TYPES
// ================================================================

// Flexible deal type that accepts any deal-like object
interface PreviewDeal {
  merchant?: string;
  headline_mechanic?: string;
  effective_percent_back?: number | null;
  is_bulk?: boolean;
  [key: string]: unknown;
}

// ================================================================
// CR406: PREVIEW-ONLY ERROR BOUNDARY
// ================================================================

interface DraftPreviewErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface DraftPreviewErrorBoundaryProps {
  children: ReactNode;
  componentKey: string;
  onReset?: () => void;
}

class DraftPreviewErrorBoundary extends Component<DraftPreviewErrorBoundaryProps, DraftPreviewErrorBoundaryState> {
  constructor(props: DraftPreviewErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): DraftPreviewErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[CR406] Draft preview crashed:", {
      componentKey: this.props.componentKey,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-destructive/30 bg-destructive/5 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <div className="space-y-1 flex-1 min-w-0">
              <p className="text-xs text-destructive font-medium">
                Preview crashed for "{this.props.componentKey}"
              </p>
              {this.state.error && (
                <code className="text-[10px] font-mono block text-destructive/70 truncate">
                  {this.state.error.message}
                </code>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={this.handleReset}
                className="h-6 text-xs gap-1 px-2"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ================================================================
// TYPES (continued)
// ================================================================

interface DraftComponentPreviewProps {
  componentKey: string;
  deal: PreviewDeal | null;
  components: ComponentsConfig;
  styling: CardStyling;
  dealTypeKey: string;
}

// ================================================================
// SIMPLIFIED PREVIEW COMPONENTS
// ================================================================

function HeaderPreview({ deal, components }: { deal: PreviewDeal; components: ComponentsConfig }) {
  const typography = components.header_front?.typography;
  
  return (
    <div 
      className="relative overflow-hidden rounded-lg bg-gradient-to-r from-slate-700 to-slate-600 text-white p-4 flex items-center gap-3"
      style={{ height: "80px" }}
    >
      {/* Logo placeholder */}
      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
        <LayoutTemplate className="h-6 w-6 text-white/60" />
      </div>
      
      {/* Title */}
      <div>
        <p 
          className="font-semibold"
          style={{
            fontFamily: typography?.fontFamily !== "inherit" ? typography?.fontFamily : undefined,
            fontSize: typography?.fontSize ? `${typography.fontSize}px` : "18px",
            fontWeight: typography?.fontWeight ?? 600,
          }}
        >
          {deal.merchant || "Merchant Name"}
        </p>
        <p className="text-sm text-white/70">Deal Card Header</p>
      </div>
    </div>
  );
}

// CR450: Header (Back) preview
function HeaderBackPreview({ deal, components }: { deal: PreviewDeal; components: ComponentsConfig }) {
  const backConfig = (components as any).header_back as Record<string, any> | undefined;
  const mode = backConfig?.mode ?? "custom";
  const customTemplate = backConfig?.custom_template ?? "{{title}}\n{{condition}}";
  const inheritFrontStyle = backConfig?.inherit_front_style ?? true;
  
  // Resolve typography
  const frontTypo = (components as any).header_front?.typography;
  const backTypo = backConfig?.typography;
  const effectiveTypo = (mode === "same_as_front" && inheritFrontStyle) ? frontTypo : backTypo;
  
  // Resolve background
  const bgConfig = backConfig?.background;
  const bgColor = bgConfig?.color_source === "custom_hex" && bgConfig?.color_hex
    ? bgConfig.color_hex
    : undefined;
  
  // Resolve title + subtitle based on mode
  let title = "";
  let subtitle = "";
  const merchantName = deal.merchant || "Merchant Name";
  const condition = deal.headline_mechanic || "Buy at store, get a bonus gift card";
  const reward = deal.effective_percent_back ? `${deal.effective_percent_back}% back` : "$10 BONUS";
  
  switch (mode) {
    case "same_as_front":
      title = merchantName;
      subtitle = "Deal Card Header";
      break;
    case "merchant_name":
      title = merchantName;
      subtitle = condition;
      break;
    case "reward_amount":
      title = reward;
      subtitle = condition;
      break;
    case "custom": {
      const rendered = customTemplate
        .replace(/\{\{title\}\}/g, merchantName)
        .replace(/\{\{merchant\}\}/g, merchantName)
        .replace(/\{\{reward\}\}/g, reward)
        .replace(/\{\{condition\}\}/g, condition)
        .replace(/\{\{deal_type\}\}/g, "BONUS");
      const lines = rendered.split("\n");
      title = lines[0] || "";
      subtitle = lines.slice(1).join(" · ");
      break;
    }
  }

  return (
    <div 
      className="relative overflow-hidden rounded-lg text-white p-4 flex items-center gap-3"
      style={{ 
        height: "80px",
        backgroundColor: bgColor,
        background: bgColor ? undefined : "linear-gradient(to right, #475569, #64748b)",
      }}
    >
      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
        <LayoutTemplate className="h-5 w-5 text-white/60" />
      </div>
      <div className="min-w-0 flex-1">
        <p 
          className="font-semibold truncate"
          style={{
            fontFamily: effectiveTypo?.fontFamily !== "inherit" ? effectiveTypo?.fontFamily : undefined,
            fontSize: effectiveTypo?.fontSize ? `${effectiveTypo.fontSize}px` : "16px",
            fontWeight: effectiveTypo?.fontWeight ?? 600,
            fontStyle: effectiveTypo?.fontStyle ?? "normal",
            color: effectiveTypo?.color_hex ?? undefined,
          }}
        >
          {title}
        </p>
        {subtitle && (
          <p className="text-sm text-white/70 truncate">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function RibbonPreview({ deal, styling, dealTypeKey, components }: { deal: PreviewDeal; styling: CardStyling; dealTypeKey: string; components: ComponentsConfig }) {
  // CR305: Use Theme Labs SSOT for ribbon color resolution
  const { tokens: globalTokens } = useGlobalTheme();
  const dealTypeThemes = globalTokens.dealTypeThemes ?? null;
  
  // CR343: Read ribbon config from draft components
  const ribbonConfig = components.ribbon as Record<string, any> | undefined;
  const merchantSubline = ribbonConfig?.merchantSubline;
  const showMerchantLogo = merchantSubline?.showLogo ?? false;
  const showMerchantName = merchantSubline?.showName ?? true;
  const sublineEnabled = merchantSubline?.enabled ?? false;
  
  // CR305: Use centralized resolver with Theme Labs
  const colorConfig: RibbonColorConfig = useMemo(() => ({
    color_source: ribbonConfig?.color_source ?? "theme_token",
    color_hex: ribbonConfig?.color_hex ?? null,
  }), [ribbonConfig?.color_source, ribbonConfig?.color_hex]);
  
  // CR306: Include styling.ribbonBaseHex as fallback before legacy defaults
  const stylingFallbackHex = (styling as any)?.ribbonBaseHex ?? null;
  
  const ribbonBg = useMemo(
    () => getResolvedRibbonHex(colorConfig, dealTypeKey as DealCategory, null, dealTypeThemes, stylingFallbackHex),
    [colorConfig, dealTypeKey, dealTypeThemes, stylingFallbackHex]
  );
  
  // CR343: Read primary text config
  const primaryConfig = ribbonConfig?.primary;
  const ribbonLabel = primaryConfig?.customLabel || dealTypeKey;
  
  return (
    <div className="relative w-40 h-40 bg-muted rounded-lg overflow-hidden border">
      <div 
        className="absolute top-0 right-0 w-32 h-32"
        style={{ 
          clipPath: "polygon(100% 0, 100% 100%, 0 0)",
          backgroundColor: ribbonBg,
        }}
      >
        <div className="absolute top-3 right-3 text-right text-white">
          <span className="text-xs uppercase tracking-wide opacity-90 block">
            {ribbonLabel}
          </span>
          {/* CR343/G4: Merchant subline with logo + name */}
          {sublineEnabled && (
            <div className="flex items-center justify-end gap-1 mt-0.5 opacity-80">
              {showMerchantLogo && (
                <div className="w-3 h-3 bg-white/30 rounded-sm" />
              )}
              {showMerchantName && (
                <span className="text-[9px] truncate max-w-[60px]">
                  {deal.merchant || "Merchant"}
                </span>
              )}
            </div>
          )}
          {!sublineEnabled && (
            <span className="text-[10px] opacity-70 block">
              {deal.merchant || "Deal"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StarburstPreview({ deal, dealTypeKey }: { deal: PreviewDeal; dealTypeKey: string }) {
  const valuePercent = deal.effective_percent_back || 15;
  
  return (
    <div className="relative">
      <ValueStarburstBadge
        valuePercent={valuePercent}
        size="sm"
        isPercent={true}
        dealType={dealTypeKey}
        forceRender={true}
      />
    </div>
  );
}

function HeadlinePreview({ deal }: { deal: PreviewDeal }) {
  return (
    <div className="p-3 bg-white rounded-lg border">
      <p className="text-sm font-medium leading-snug">
        {deal.headline_mechanic || `Buy a gift card at ${deal.merchant || "Store"}, get a bonus gift card.`}
      </p>
    </div>
  );
}

function BadgePreview() {
  return (
    <div className="flex flex-wrap gap-2 p-3 bg-white rounded-lg border">
      <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
        <Star className="w-3 h-3" />
        Membership
      </span>
      <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
        Corporate
      </span>
    </div>
  );
}

function PillsPreview({ deal }: { deal: PreviewDeal }) {
  return (
    <div className="space-y-1 text-sm text-black/70 bg-black/5 rounded-lg p-3">
      <p>Spend $50, get $5 bonus</p>
      <p>Spend $100, get $15 bonus</p>
      {deal.is_bulk && (
        <p className="text-xs font-medium text-black/60 mt-1">Bulk deal</p>
      )}
    </div>
  );
}

function ValueBoxPreview({ dealTypeKey }: { dealTypeKey: string }) {
  const colors: Record<string, { bg: string; text: string; icon: string }> = {
    BONUS: { bg: "bg-primary/10", text: "text-primary", icon: "text-primary" },
    DISCOUNT: { bg: "bg-orange-50", text: "text-orange-700", icon: "text-orange-600" },
    POINTS: { bg: "bg-purple-50", text: "text-purple-700", icon: "text-purple-600" },
    IN_KIND: { bg: "bg-amber-50", text: "text-amber-700", icon: "text-amber-600" },
    UNCLASSIFIED: { bg: "bg-muted", text: "text-foreground", icon: "text-muted-foreground" },
  };
  
  const { bg, text, icon } = colors[dealTypeKey] || colors.UNCLASSIFIED;
  
  return (
    <div className={cn("flex items-center justify-between py-2 px-3 rounded-md border", bg)}>
      <div className="flex items-center gap-2">
        <TrendingUp className={cn("h-4 w-4", icon)} />
        <span className={cn("text-sm font-medium", text)}>
          Effective Value
        </span>
      </div>
      <span className={cn("text-lg font-semibold", text)}>
        ≈ 15% back
      </span>
    </div>
  );
}

function SpendBoxPreview() {
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-md border">
      <div className="flex items-center gap-2">
        <DollarSign className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Qualifying Spend</span>
      </div>
      <span className="text-lg font-semibold">$50 CAD</span>
    </div>
  );
}

function RewardBoxPreview() {
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-md border border-green-200">
      <div className="flex items-center gap-2">
        <Gift className="h-4 w-4 text-green-600" />
        <span className="text-sm font-medium text-green-700">Bonus Reward</span>
      </div>
      <span className="text-lg font-semibold text-green-700">$10 CAD</span>
    </div>
  );
}

function DatesPreview() {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground p-2">
      <Calendar className="h-3.5 w-3.5" />
      <span>Jan 15 - Feb 28, 2025</span>
    </div>
  );
}

function CTAPreview() {
  return (
    <button className="rounded-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5">
      <MousePointer className="w-3.5 h-3.5" />
      View Details
    </button>
  );
}

function ActionsPreview() {
  return (
    <div className="flex flex-wrap gap-2">
      <a className="rounded-full px-4 py-2 text-sm font-medium bg-black text-white inline-flex items-center gap-1.5">
        View deal
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
      <a className="rounded-full px-4 py-2 text-sm font-medium bg-black/5 text-black inline-flex items-center gap-1.5">
        T&Cs
      </a>
    </div>
  );
}

function FeedbackPreview({ components }: { components: ComponentsConfig }) {
  const [state, setState] = useState<"pending" | "up" | "down">("pending");
  const config = (components.feedback ?? {}) as {
    pendingLabel?: string;
    positiveLabel?: string;
    negativeLabel?: string;
  };

  const labels = {
    pending: config.pendingLabel?.trim() || "You decide",
    up: config.positiveLabel?.trim() || "Worth it",
    down: config.negativeLabel?.trim() || "Not",
  };

  const badgeClass = state === "up"
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : state === "down"
      ? "bg-red-100 text-red-800 border-red-300"
      : "bg-white text-slate-700 border-slate-200";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-sm inline-flex items-center gap-2">
      <div className={cn("rounded-full border px-4 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5", badgeClass)}>
        {state === "up" && <CheckCircle2 className="w-4 h-4" />}
        {state === "down" && <XCircle className="w-4 h-4" />}
        {state === "pending" ? labels.pending : state === "up" ? labels.up : labels.down}
      </div>
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          className={cn(
            "h-8 w-8 rounded-md border border-transparent inline-flex items-center justify-center",
            state === "up" ? "bg-slate-900 text-white" : "text-slate-800 hover:bg-slate-100"
          )}
          onClick={() => setState("up")}
          aria-label="Thumbs up"
        >
          <ThumbsUp className="w-4 h-4" />
        </button>
        <button
          type="button"
          className={cn(
            "h-8 w-8 rounded-md border border-transparent inline-flex items-center justify-center",
            state === "down" ? "bg-red-600 text-white" : "text-slate-800 hover:bg-slate-100"
          )}
          onClick={() => setState("down")}
          aria-label="Thumbs down"
        >
          <ThumbsDown className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="h-8 w-8 rounded-md border border-transparent inline-flex items-center justify-center text-slate-800 hover:bg-slate-100"
          onClick={() => setState("pending")}
          aria-label="Reset"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ================================================================
// MAIN COMPONENT
// ================================================================

export function DraftComponentPreview({
  componentKey,
  deal,
  components,
  styling,
  dealTypeKey,
}: DraftComponentPreviewProps) {
  if (!deal) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Info className="h-5 w-5 mx-auto mb-2" />
        <p className="text-sm">Select a sample deal to preview this component</p>
      </div>
    );
  }

  return (
    <DraftPreviewErrorBoundary componentKey={componentKey}>
      <DraftPreviewContent
        componentKey={componentKey}
        deal={deal}
        components={components}
        styling={styling}
        dealTypeKey={dealTypeKey}
      />
    </DraftPreviewErrorBoundary>
  );
}

// ================================================================
// INNER PREVIEW CONTENT (wrapped by ErrorBoundary)
// ================================================================

function DraftPreviewContent({
  componentKey,
  deal,
  components,
  styling,
  dealTypeKey,
}: {
  componentKey: string;
  deal: PreviewDeal;
  components: ComponentsConfig;
  styling: CardStyling;
  dealTypeKey: string;
}) {
  const renderPreview = (): React.ReactNode => {
    switch (componentKey) {
      case "header":
        return <HeaderPreview deal={deal} components={components} />;
      
      case "header_back":
        return <HeaderBackPreview deal={deal} components={components} />;
      
      case "ribbon":
        return <RibbonPreview deal={deal} styling={styling} dealTypeKey={dealTypeKey} components={components} />;
      
      case "starburst":
        return <StarburstPreview deal={deal} dealTypeKey={dealTypeKey} />;
      
      case "headline":
        return <HeadlinePreview deal={deal} />;
      
      case "promo_badges":
        return <BadgePreview />;
      
      case "details_pills":
        return <PillsPreview deal={deal} />;
      
      case "value_box":
        return <ValueBoxPreview dealTypeKey={dealTypeKey} />;
      
      case "spend_box":
        return <SpendBoxPreview />;
      
      case "reward_box":
        return <RewardBoxPreview />;
      
      case "dates":
        return <DatesPreview />;
      
      case "cta_button":
        return <CTAPreview />;
      
      case "actions":
        return <ActionsPreview />;
      case "feedback":
        return <FeedbackPreview components={components} />;
      
      default:
        return (
          <div className="p-4 text-center text-muted-foreground">
            <AlertCircle className="h-5 w-5 mx-auto mb-2" />
            <p className="text-sm">Preview not available for this component</p>
          </div>
        );
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[100px]">
      {renderPreview()}
    </div>
  );
}

export default DraftComponentPreview;
